/**
 * googleCalendar.ts — 직원별 Google 캘린더 연결(내 업무 › 일정·할 일)의 타입·변환.
 * 설계: docs/design/11_Google캘린더_연결설계.md · 서버: backend/functions/src/googleCalendar.ts
 *
 * 저장(서버가 씀, 본인만 읽음):
 *   users/{uid}/google_link/state   연결 상태·캘린더 목록
 *   users/{uid}/google_events/{해시} 일정 사본(-60일 ~ +365일)
 * 추가·수정·삭제·동기화는 google_calendar_ops 요청 문서로만(services/googleCalendar.repository.ts).
 *
 * 순수 타입·계산만. Firebase import 금지(domain 규칙).
 */
import type { PersonalEvent } from "./personalEvent";

/** 웹 클라이언트 ID — 비밀 아님. 서버 GOOGLE_OAUTH_CLIENT_ID 와 같은 값 */
export const GOOGLE_OAUTH_CLIENT_ID =
  (import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID as string | undefined) ||
  "238269550607-d801a4buf3trd3sei4umohue3sof823j.apps.googleusercontent.com";

/** 요청 범위 — 서버 link() 의 확인 목록과 맞춘다 */
export const GOOGLE_CALENDAR_SCOPES = [
  "openid",
  "email",
  "https://www.googleapis.com/auth/calendar.calendarlist.readonly",
  "https://www.googleapis.com/auth/calendar.events",
].join(" ");

/** 서버가 받는 요청 종류 — rules google_calendar_ops action 목록과 동기 */
export type GoogleCalendarOpAction = "link" | "unlink" | "sync" | "create" | "update" | "delete" | "set_calendars";

/** Google 일정 메모 최대 길이(서버 MEMO_MAX 와 같음). ERP 개인 일정(500자)보다 길다 */
export const GOOGLE_MEMO_MAX = 2000;

export interface GoogleCalendarInfo {
  id: string;
  name: string;
  /** Google 이 정한 캘린더 색 (데이터 값 — 화면 점·줄 색에만 씀) */
  color: string;
  access_role: string;
  primary: boolean;
  /** Google 화면에서 표시 중인지 — 우리 화면 기본 표시 여부 */
  selected: boolean;
}

export type GoogleLinkStatus = "linked" | "needs_relink";

export interface GoogleLinkState {
  status: GoogleLinkStatus;
  email: string;
  /** Google 계정의 캘린더 전체(가져오기 선택 후보) */
  calendars: GoogleCalendarInfo[];
  /** 가져오기로 고른 캘린더 ID(서버 정본, 2026-10-29). 없으면 아직 안 고른 것(=옛 연결, 전체 동기화) */
  imported?: string[];
  /** epoch ms */
  last_sync_at?: number;
  last_error?: string;
}

export interface GoogleEvent {
  /** 사본 문서 ID (해시) — 수정·삭제 요청에 그대로 보낸다 */
  id: string;
  calendar_id: string;
  title: string;
  /** 시작 날짜 YYYY-MM-DD (KST) */
  date: string;
  /** 마지막 날짜(포함). 하루짜리면 date 와 같음 */
  end_date: string;
  all_day: boolean;
  start_time?: string;
  end_time?: string;
  memo: string;
  location: string;
  html_link: string;
  /** 반복 일정의 한 회차 */
  recurring: boolean;
  /** 이 계정으로 고칠 수 있는지(캘린더 쓰기 권한 + 내가 만든 일정) */
  can_edit: boolean;
}

const YMD_RE = /^\d{4}-\d{2}-\d{2}$/;
const HM_RE = /^([01]\d|2[0-3]):[0-5]\d$/;
const str = (v: unknown) => (typeof v === "string" ? v : "");

function toMs(v: unknown): number | undefined {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (v && typeof v === "object" && "toMillis" in v && typeof (v as { toMillis: unknown }).toMillis === "function") {
    return (v as { toMillis: () => number }).toMillis();
  }
  return undefined;
}

function isLinkStatus(v: unknown): v is GoogleLinkStatus {
  return v === "linked" || v === "needs_relink";
}

/** 상태 문서 → GoogleLinkState. 문서가 없거나 상태 값이 이상하면 null(= 연결 안 됨) */
export function coerceLinkState(data: Record<string, unknown> | undefined): GoogleLinkState | null {
  if (!data || !isLinkStatus(data.status)) return null;
  const cals = Array.isArray(data.calendars) ? data.calendars : [];
  return {
    status: data.status,
    email: str(data.email),
    calendars: cals
      .filter((c): c is Record<string, unknown> => !!c && typeof c === "object" && typeof (c as { id?: unknown }).id === "string")
      .map((c) => ({
        id: c.id as string,
        name: str(c.name) || (c.id as string),
        color: str(c.color),
        access_role: str(c.access_role) || "reader",
        primary: c.primary === true,
        selected: c.selected === true,
      })),
    imported: Array.isArray(data.imported) ? data.imported.filter((x): x is string => typeof x === "string") : undefined,
    last_sync_at: toMs(data.last_sync_at),
    last_error: str(data.last_error) || undefined,
  };
}

/** 가져온 캘린더 목록 — imported 가 없으면(옛 연결) 전체 */
export function importedCalendars(state: GoogleLinkState | null): GoogleCalendarInfo[] {
  if (!state) return [];
  if (!state.imported) return state.calendars;
  const set = new Set(state.imported);
  return state.calendars.filter((c) => set.has(c.id));
}

/** 사본 문서 → GoogleEvent. 날짜가 깨진 문서는 null */
export function coerceGoogleEvent(id: string, d: Record<string, unknown>): GoogleEvent | null {
  if (!YMD_RE.test(str(d.date)) || !str(d.calendar_id)) return null;
  const date = str(d.date);
  const end = YMD_RE.test(str(d.end_date)) && str(d.end_date) >= date ? str(d.end_date) : date;
  const allDay = d.all_day === true || !HM_RE.test(str(d.start_time));
  return {
    id,
    calendar_id: str(d.calendar_id),
    title: str(d.title) || "(제목 없음)",
    date,
    end_date: end,
    all_day: allDay,
    start_time: allDay ? undefined : str(d.start_time),
    end_time: !allDay && HM_RE.test(str(d.end_time)) ? str(d.end_time) : undefined,
    memo: str(d.memo),
    location: str(d.location),
    html_link: str(d.html_link),
    recurring: d.recurring === true,
    can_edit: d.can_edit === true,
  };
}

export const isMultiDay = (ev: Pick<GoogleEvent, "date" | "end_date">) => ev.end_date !== ev.date;

/** 일정이 걸친 날짜들 (달력 칸마다 보이게). 너무 긴 일정은 62일까지만 */
export function googleEventDays(ev: Pick<GoogleEvent, "date" | "end_date">): string[] {
  const out: string[] = [];
  const [y, m, d] = ev.date.split("-").map(Number);
  for (let i = 0; i < 62; i++) {
    const dt = new Date(Date.UTC(y, m - 1, d + i));
    const ymd = `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, "0")}-${String(dt.getUTCDate()).padStart(2, "0")}`;
    if (ymd > ev.end_date) break;
    out.push(ymd);
  }
  return out;
}

/** Google 설명(HTML 이 섞일 수 있음) → 화면용 평문 */
export function plainMemo(s: string): string {
  return s
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|li)>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, "&")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/** 수정 모달이 같은 폼을 쓰도록 PersonalEvent 모양으로 (kind = 일정) */
export function googleEventAsPersonal(ev: GoogleEvent): PersonalEvent {
  return {
    id: ev.id,
    kind: "event",
    done: false,
    title: ev.title,
    date: ev.date,
    all_day: ev.all_day,
    start_time: ev.start_time,
    end_time: ev.end_time,
    memo: plainMemo(ev.memo) || undefined,
  };
}

export const canWriteCalendar = (c: GoogleCalendarInfo) => c.access_role === "owner" || c.access_role === "writer";

/** 수정 불가 사유 (없으면 null) */
export function googleReadOnlyReason(ev: GoogleEvent): string | null {
  if (!ev.can_edit) return "이 일정은 읽기 전용입니다(다른 사람이 만든 일정이거나 읽기 권한만 있는 캘린더).";
  if (isMultiDay(ev)) return "여러 날에 걸친 일정은 Google 캘린더에서 수정하세요.";
  return null;
}

/** 날짜 → 종일 먼저 → 시작 시각 → 제목 */
export function sortGoogleEvents(list: GoogleEvent[]): GoogleEvent[] {
  return [...list].sort((a, b) => {
    if (a.date !== b.date) return a.date < b.date ? -1 : 1;
    if (a.all_day !== b.all_day) return a.all_day ? -1 : 1;
    const sa = a.start_time ?? "";
    const sb = b.start_time ?? "";
    if (sa !== sb) return sa < sb ? -1 : 1;
    return a.title.localeCompare(b.title, "ko");
  });
}

/** 칸·목록 시간 표시 — "종일" / "09:00" / "09:00–10:30" / 여러 날이면 "~10/25" */
export function googleTimeLabel(ev: GoogleEvent): string {
  if (isMultiDay(ev)) {
    const [, m, d] = ev.end_date.split("-");
    return `~${Number(m)}/${Number(d)}`;
  }
  if (ev.all_day || !ev.start_time) return "종일";
  return ev.end_time ? `${ev.start_time}–${ev.end_time}` : ev.start_time;
}
